import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, hasWrite, isApprover, isAdmin } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FuelLog } from "./FuelLog";
import { MonthlyFuelReport } from "./MonthlyFuelReport";

const ctl = "h-10 px-2 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const COLS = "grid grid-cols-2 md:grid-cols-[100px_110px_minmax(0,1fr)_100px_100px_90px_minmax(0,1fr)] gap-2 md:gap-4 px-4 md:px-5";
const km = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN"));

const exportCsv = (trips) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Date", "Gate pass", "Vehicle", "From", "To", "Start km", "Next km", "Km run", "Driver", "Driver mobile"].join(","),
    ...trips.map((t) => [t.date, t.number, t.vehicle_number, t.from_location, t.to_location, t.start_km, t.end_km, t.km_run, t.driver_name, t.driver_mobile].map(esc).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  a.download = "vehicle_trips.csv"; a.click(); URL.revokeObjectURL(a.href);
};

const TripRow = ({ t }) => (
  <div data-testid={`trip-row-${t.id}`} className={`${COLS} py-3 items-center text-sm`}>
    <span className="num text-xs">{t.date}</span>
    <span className="num font-semibold">{t.vehicle_number}</span>
    <div className="min-w-0 col-span-2 md:col-span-1 break-words">{t.from_location || "—"} → <b>{t.to_location || "—"}</b><div className="num text-[11px] text-muted-foreground">{t.number}</div></div>
    <span className="num text-xs">Start {km(t.start_km)}</span>
    <span className="num text-xs">Next {km(t.end_km)}</span>
    <span data-testid={`trip-km-${t.id}`} className={`num font-bold ${t.km_run < 0 ? "text-destructive" : ""}`}>{t.km_run === null ? "open" : `${km(t.km_run)} km`}</span>
    <div className="min-w-0 col-span-2 md:col-span-1"><div className="font-medium break-words">{t.driver_name}</div><div className="num text-xs text-muted-foreground">{t.driver_mobile}</div></div>
  </div>
);

export const VehicleTrips = () => {
  const [f, setF] = useState({ vehicle: "", start: "", end: "" });
  const [vehicles, setVehicles] = useState([]);
  const [data, setData] = useState({ trips: [], summary: [] });
  const [fuel, setFuel] = useState([]);
  const [rk, setRk] = useState(0);
  const [upi, setUpi] = useState({ upi_id: "", name: "" });
  const { user } = useAuth();
  useEffect(() => { api.get("/gatepass/meta").then((r) => { setVehicles(r.data.vehicles || []); setUpi(r.data.fuel_upi || { upi_id: "", name: "" }); }).catch((e) => console.error(e)); }, []);
  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try {
      const [t, fl] = await Promise.all([api.get("/gatepass/trips", { params }), api.get("/gatepass/fuel", { params })]);
      setData(t.data); setFuel(fl.data); setRk((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  return (
    <section data-testid="vehicle-trips">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <select data-testid="trip-vehicle" className={ctl} value={f.vehicle} onChange={set("vehicle")}>
          <option value="">All company vehicles</option>
          {vehicles.map((v) => <option key={v.number} value={v.number}>{v.number}{v.name ? ` · ${v.name}` : ""}</option>)}
        </select>
        <input data-testid="trip-start" type="date" className={`${ctl} num`} value={f.start} onChange={set("start")} />
        <input data-testid="trip-end" type="date" className={`${ctl} num`} value={f.end} onChange={set("end")} />
        <button data-testid="trip-export" onClick={() => exportCsv(data.trips)} className="h-10 px-3 rounded-md border border-input bg-card text-sm flex items-center gap-1.5 hover:bg-muted transition-colors"><Download size={14} /> Download CSV</button>
      </div>
      {data.summary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {data.summary.map((s) => (
            <div key={s.vehicle_number} data-testid={`trip-sum-${s.vehicle_number}`} className={`rounded-lg border px-4 py-3 ${s.low_mileage ? "border-destructive bg-destructive/10" : "border-border bg-card"}`}>
              <div className="num text-xs text-muted-foreground">{s.vehicle_number}</div>
              <div className="num text-lg font-bold">{km(s.km)} km</div>
              <div className="text-xs text-muted-foreground">{s.trips} trip{s.trips === 1 ? "" : "s"} · {s.litres} L</div>
              <div data-testid={`trip-kmpl-${s.vehicle_number}`} className={`num text-sm font-semibold ${s.low_mileage ? "text-destructive" : "text-primary"}`}>{s.km_per_litre ? `${s.km_per_litre} km/L` : "km/L —"}</div>
              {s.usual_kmpl ? <div className="text-[11px] text-muted-foreground">usual {s.usual_kmpl} km/L</div> : null}
              {s.low_mileage && <div data-testid={`trip-low-${s.vehicle_number}`} className="text-[11px] font-bold text-destructive flex items-center gap-1"><AlertTriangle size={12} /> Low mileage — check vehicle / fuel</div>}
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mb-3">Km run = odometer at the vehicle's next gate pass minus this trip's reading. The latest trip shows "open" until the next pass is made. A vehicle turns red when its km/L is more than 20% below its usual fill-to-fill average.</p>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className={`${COLS} hidden md:grid py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground`}>
          <span>Date</span><span>Vehicle</span><span>From → To</span><span>Start km</span><span>Next km</span><span>Km run</span><span>Driver</span>
        </div>
        {data.trips.length === 0 ? <div className="p-10 text-center text-sm text-muted-foreground">No company vehicle trips yet.</div>
          : <div className="divide-y divide-border">{data.trips.map((t) => <TripRow key={t.id} t={t} />)}</div>}
      </div>
      <FuelLog vehicles={vehicles} entries={fuel} canWrite={hasWrite(user)} canDelete={isApprover(user)} isAdmin={isAdmin(user) || user?.platform_role === "superadmin"} upi={upi} onUpiSaved={setUpi} onChanged={load} />
      <MonthlyFuelReport vehicle={f.vehicle} refreshKey={rk} />
    </section>
  );
};
